import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Stats } from "@/components/site/Stats";
import { IMG, PRODUCTS } from "@/lib/content";
import { Price } from "@/components/site/Price";

const Frame = ({ src, srcMd, alt, to, label, className = "", testId }) => (
  <Link to={to} data-testid={testId} className={`group relative block overflow-hidden border border-line bg-[#0b0b0b] ${className}`}>
    <picture className="block h-full">
      {srcMd && <source media="(min-width: 768px) and (max-width: 1279px)" srcSet={srcMd} />}
      <motion.img src={src} alt={alt} loading="lazy" initial={{ scale: 1.12 }} whileInView={{ scale: 1 }} viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="h-full w-full object-cover transition-[filter] duration-700 group-hover:brightness-110" />
    </picture>
    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/90 to-transparent p-6">
      <span className="font-display text-lg text-white">{label}</span>
      <ArrowUpRight size={20} className="text-white transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
    </div>
  </Link>
);

export const ProductReveal = () => (
  <section data-testid="product-reveal-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-40">
    <Reveal className="grid gap-6 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <p className="eyebrow">The system</p>
        <h2 className="font-display mt-5 text-4xl font-medium leading-tight tracking-tight text-white md:text-6xl">
          One method to learn. <span className="font-serif-i text-zinc-400">One workbook to act.</span>
        </h2>
      </div>
      <p className="text-zinc-400 md:col-span-5 md:text-lg">Most courses stop at inspiration. First Mover OS gives you the thinking and the worksheets to turn it into decisions.</p>
    </Reveal>
    <div className="mt-16 grid gap-6 md:grid-cols-2 xl:h-[720px] xl:grid-cols-12 xl:grid-rows-2">
      <Reveal className="h-full xl:col-span-5 xl:row-span-2">
        <Frame testId="reveal-ebook" src={IMG.ebookTall} srcMd={IMG.ebook} alt="The First Mover Blueprint eBook" to="/products/ebook" label={PRODUCTS.ebook.name} className="aspect-[3/4] h-full md:aspect-[3/2] xl:aspect-auto" />
      </Reveal>
      <Reveal delay={0.1} className="h-full xl:col-span-7">
        <Frame testId="reveal-workbook" src={IMG.workbook} alt="The First Mover Workbook" to="/products/workbook" label={PRODUCTS.workbook.name} className="aspect-[3/2] h-full xl:aspect-auto" />
      </Reveal>
      <Reveal delay={0.2} className="h-full md:col-span-2 xl:col-span-7">
        <Link to="/products/bundle" data-testid="reveal-bundle-link"
          className="group grid h-full overflow-hidden border border-cta bg-[#0a0f1f] sm:grid-cols-2">
          <div className="relative aspect-[3/2] overflow-hidden sm:self-center xl:aspect-auto xl:self-stretch" data-testid="reveal-bundle-image">
            <picture>
              <source media="(min-width: 1280px)" srcSet={IMG.bundleSquare} />
              <img src={IMG.bundle} alt={PRODUCTS.bundle.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
            </picture>
          </div>
          <div className="flex flex-col justify-between p-7 md:p-8">
            <div>
              <p className="eyebrow !text-white"><span className="mr-2 inline-block h-1.5 w-1.5 bg-cta align-middle" />Recommended · Bundle</p>
              <p className="font-display mt-4 text-2xl leading-snug text-white">{PRODUCTS.bundle.name}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">Read a lesson, then fill the matching worksheet the same evening. eBook + Workbook in one.</p>
            </div>
            <div className="mt-6 flex items-end justify-between">
              <span className="text-sm text-zinc-300"><span className="font-display text-xl text-white"><Price role="bundle" /></span><br />or 2 × <Price role="bundle" installment /></span>
              <ArrowUpRight size={22} className="text-white transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </div>
          </div>
        </Link>
      </Reveal>
    </div>
    <Reveal className="mt-6"><Stats /></Reveal>
  </section>
);
